import { Area, ComposedChart, Legend, Line, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import { useTheme } from 'styled-components'
import { useCallback, useMemo, useState } from 'react'

import { ChartTooltip } from 'components/ChartTooltip'
import { getDisplayTimestamp, isSameDay } from 'utils/helpers'
import { useIsMobile } from 'utils/useIsMobile'
import { getYDomainWithPadding, getHistoryMetricsConfig, bridgeBaseKey } from './utils'
import { addBoundaryNullsForGaps, getChartTicks, POSITION_METRICS_GAP_THRESHOLD_MS } from 'utils/chartGapUtils'
import { LegendContent } from './LegendContent'
import { getFormattedUsdValue } from 'utils/usdFormatters'
import { CustomTick } from './CustomTick'

export type DataPoint = {
  timestampMs: number
  [key: string]: number | undefined
}

const ANIMATION_DURATION = 300
const DESKTOP_GRAPH_HEIGHT = 560
const MOBILE_TICKS = 3
const DESKTOP_TICKS = 8

interface Props {
  data: DataPoint[]
  /** Synthetic dashed "bridge to now" series keys (see buildChartSeries). */
  bridgeKeys?: string[]
  xSymbol: string
  ySymbol: string
}

const EMPTY_BRIDGE_KEYS: string[] = []

export const MetricsHistoryGraph = ({ data, bridgeKeys = EMPTY_BRIDGE_KEYS, xSymbol, ySymbol }: Props) => {
  const isMobile = useIsMobile()
  const theme = useTheme()

  const [hiddenKeys, setHiddenKeys] = useState<string[]>([])

  const handleLegendClick = useCallback(
    (dataKey: string) =>
      setHiddenKeys(prev => (prev.includes(dataKey) ? prev.filter(k => k !== dataKey) : [...prev, dataKey])),
    []
  )

  const valueKeys = useMemo(
    () => Object.keys(data[0]).filter(key => key !== 'timestampMs' && !bridgeKeys.includes(key)),
    [data, bridgeKeys]
  )
  const visibleKeys = valueKeys.filter(key => !hiddenKeys.includes(key))
  // Bridge series inherit visibility from their base series.
  const visibleBridgeKeys = bridgeKeys.filter(key => !hiddenKeys.includes(bridgeBaseKey(key)))
  const isPercentage = visibleKeys.every(key => getHistoryMetricsConfig(theme)[key]?.isPct)
  const yDomain = getYDomainWithPadding(data, [...visibleKeys, ...visibleBridgeKeys])

  const ticks = useMemo(() => getChartTicks(data, isMobile ? MOBILE_TICKS : DESKTOP_TICKS), [data, isMobile])
  const chartData = useMemo(
    () => addBoundaryNullsForGaps(data, valueKeys, POSITION_METRICS_GAP_THRESHOLD_MS),
    [data, valueKeys]
  )

  // Points whose neighbors are both missing render invisibly (areas draw no
  // segment and dots are off) — collect them per key so those points get an
  // explicit dot. Gap-boundary null rows make sparse points "isolated" here.
  const isolatedIndexesByKey = useMemo(() => {
    const result: Record<string, Set<number>> = {}
    for (const key of valueKeys) {
      const isolated = new Set<number>()
      for (let i = 0; i < chartData.length; i++) {
        const value = chartData[i][key]
        const prev = i > 0 ? chartData[i - 1][key] : null
        const next = i < chartData.length - 1 ? chartData[i + 1][key] : null
        if (typeof value === 'number' && prev == null && next == null) {
          isolated.add(i)
        }
      }
      if (isolated.size > 0) {
        result[key] = isolated
      }
    }
    return result
  }, [chartData, valueKeys])

  const svgHeight = Math.min(window.innerHeight * 0.5, DESKTOP_GRAPH_HEIGHT)
  const isSingleDayRange = ticks && ticks.length > 0 ? isSameDay(ticks[0], ticks[ticks.length - 1]) : false

  const renderIsolatedDot = (key: string, stroke: string) => {
    const isolated = isolatedIndexesByKey[key]
    return (props: { cx?: number; cy?: number; index?: number }) => {
      const { cx, cy, index } = props
      if (index !== undefined && isolated?.has(index) && cx != null && cy != null) {
        return <circle key={`${key}-dot-${index}`} cx={cx} cy={cy} r={3} fill={stroke} />
      }
      return <g key={`${key}-dot-${index}`} />
    }
  }

  return (
    <ResponsiveContainer width="100%" height={svgHeight} style={{ marginTop: 24 }}>
      <ComposedChart
        data={chartData}
        barCategoryGap={0}
        barGap={0}
        margin={{
          left: 24,
          right: 24,
        }}
      >
        <XAxis
          dataKey="timestampMs"
          tick={({ x, y, payload, index }) => (
            <CustomTick
              x={Number(x)}
              y={Number(y)}
              payload={payload}
              isSingleDayRange={isSingleDayRange}
              index={index}
            />
          )}
          domain={[data[0].timestampMs, data[data.length - 1].timestampMs]}
          type="number"
          height={50}
          ticks={ticks}
          axisLine={{ stroke: theme.colors.text.primaryDark }}
          interval={0}
          tickLine={{
            stroke: theme.colors.text.primaryDark,
            strokeWidth: 2,
          }}
        />
        <YAxis
          domain={yDomain}
          tickFormatter={val => (isPercentage ? `${val.toFixed(2)}%` : getFormattedUsdValue(val))}
          tick={{ fontSize: isMobile ? 12 : 14, fill: theme.colors.text.primary }}
          orientation="right"
        />
        <ChartTooltip
          contentStyle={{ backgroundColor: theme.colors.background.primary, border: 'none', fontSize: 14 }}
          labelFormatter={(label: number) => getDisplayTimestamp(label)}
          formatter={(value: number, name: string, entry) => {
            if (value == null) {
              return null
            }
            let key = entry?.dataKey as string
            const isBridge = bridgeKeys.includes(key)
            if (isBridge) {
              key = bridgeBaseKey(key)
              // At the anchor point the real series already shows this value —
              // only surface the bridge entry at the synthetic "now" point.
              if (typeof (entry?.payload as DataPoint | undefined)?.[key] === 'number') {
                return null
              }
            }
            if (hiddenKeys.includes(key)) {
              return null
            }
            const config = getHistoryMetricsConfig(theme, xSymbol, ySymbol)[key]
            if (!config) {
              return [value, name]
            }
            const displayValue = config.isPct ? `${value.toFixed(2)}%` : getFormattedUsdValue(value)

            return [displayValue, isBridge ? `${config.label} (live)` : config.label]
          }}
        />
        <Legend
          verticalAlign="top"
          height={36}
          iconType="line"
          content={
            <LegendContent
              xSymbol={xSymbol}
              ySymbol={ySymbol}
              valueKeys={valueKeys}
              hiddenKeys={hiddenKeys}
              onClick={handleLegendClick}
            />
          }
        />
        <defs>
          {valueKeys.map(key => {
            const stroke = getHistoryMetricsConfig(theme)[key]?.stroke ?? theme.colors.text.primary
            return (
              <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={stroke} stopOpacity={0.7} />
                <stop offset="100%" stopColor={stroke} stopOpacity={0} />
              </linearGradient>
            )
          })}
        </defs>
        {valueKeys.map(key => {
          if (hiddenKeys.includes(key)) {
            return null
          }
          const config = getHistoryMetricsConfig(theme)[key]
          const stroke = config?.stroke ?? theme.colors.text.primary
          return (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              name={config?.label ?? key}
              stroke={stroke}
              fill={`url(#gradient-${key})`}
              fillOpacity={1}
              animationDuration={ANIMATION_DURATION}
              dot={isolatedIndexesByKey[key] ? renderIsolatedDot(key, stroke) : false}
            />
          )
        })}
        {visibleBridgeKeys.map(key => {
          const baseKey = bridgeBaseKey(key)
          const stroke = getHistoryMetricsConfig(theme)[baseKey]?.stroke ?? theme.colors.text.primary
          return (
            <Line
              key={key}
              type="linear"
              dataKey={key}
              stroke={stroke}
              strokeDasharray="5 4"
              strokeWidth={1.5}
              connectNulls
              dot={{ r: 2.5, fill: stroke, strokeWidth: 0 }}
              activeDot={{ r: 3.5 }}
              legendType="none"
              animationDuration={ANIMATION_DURATION}
            />
          )
        })}
      </ComposedChart>
    </ResponsiveContainer>
  )
}
